import {Observable, combineLatest} from 'rxjs';
import {mergeMap, switchMap, map, take} from 'rxjs/operators';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {inject} from '@angular/core';
import {LanguageService} from '../../language/language.service';
import {LayoutService} from '../../layout/layout.service';
import {Endpoint} from '../../layout/endpoint';
import {FilterResult} from '../../resource/filterresult';
import {
  EnvironmentConfigService,
  EnvironmentAttributes,
  EnvironmentObjectTypes,
} from './environment-config.service';

export abstract class BaseApiService {
  protected readonly http = inject(HttpClient);
  protected readonly languageService = inject(LanguageService);
  protected readonly layoutService = inject(LayoutService);
  protected readonly envConfig = inject(EnvironmentConfigService);

  protected readonly headers = new HttpHeaders({'Content-Type': 'application/json'});

  constructor() {}

  /**
   * Get attribute name for current environment
   */
  protected getAttr(key: keyof EnvironmentAttributes): string | null {
    return this.envConfig.getAttributeName(key);
  }

  /**
   * Get object type name for current environment
   */
  protected getType(key: keyof EnvironmentObjectTypes): string | null {
    return this.envConfig.getObjectTypeName(key);
  }

  protected getHttpOptions(endpoint: Endpoint, additionalOptions?: any): any {
    const baseOptions: any = {
      headers: this.headers,
    };

    // Add credentials for OnPrem mode
    if (!endpoint.isCloud()) {
      baseOptions.withCredentials = true;
    }

    // Merge with additional options if provided
    return additionalOptions ? {...baseOptions, ...additionalOptions} : baseOptions;
  }

  protected getWithLocale<T = FilterResult>(
    filter: string,
    attributes: string[],
    pageSize: number = 1000,
    localeConfig?: Observable<any>,
  ): Observable<T> {
    // If locale config is provided (for non-SupportedLocaleConfigurationService services), use it
    // Otherwise, just use the language service without locale config (for SupportedLocaleConfigurationService itself)
    if (localeConfig) {
      return combineLatest([
        this.languageService.getLanguage(localeConfig),
        this.layoutService.endpoint$.pipe(take(1)),
      ]).pipe(
        switchMap(([language, env]) => {
          const params = this.buildStandardParams(filter, attributes, pageSize, language);
          const options = {
            headers: this.headers,
            params: params,
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          };
          return this.http.get<T>(env.getUrl() + 'v2/resources/', options);
        }),
      );
    } else {
      // For SupportedLocaleConfigurationService, don't need locale
      return this.layoutService.endpoint$.pipe(
        take(1),
        switchMap((env: Endpoint) => {
          const params = this.buildStandardParams(filter, attributes, pageSize, null);
          const options = {
            headers: this.headers,
            params: params,
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          };
          return this.http.get<T>(env.getUrl() + 'v2/resources/', options);
        }),
      );
    }
  }

  protected buildStandardParams(
    filter: string,
    attributes: string[],
    pageSize: number,
    locale: string,
  ): HttpParams {
    let params = new HttpParams()
      .append('filter', filter)
      .append('attributes', attributes.join(','))
      .append('pageSize', pageSize.toString());

    if (locale && locale !== '*' && locale !== 'NeutralLocale') {
      params = params.append('locale', locale);
    }

    return params;
  }

  protected buildSimpleParams(filter: string): HttpParams {
    return new HttpParams().append('filter', filter);
  }

  protected getDirectUrl<T>(url: string, params?: HttpParams): Observable<T> {
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options = {
          headers: this.headers,
          params: params,
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        return this.http.get<T>(url, options);
      }),
    );
  }

  protected postToUrl<T>(url: string, body: any): Observable<T> {
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options: any = {
          headers: this.headers,
          observe: 'response',
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        return this.http.post<T>(url, JSON.stringify(body), options);
      }),
    ) as any;
  }

  protected patchUrl<T>(url: string, body: any, params?: HttpParams): Observable<T> {
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options: any = {
          headers: this.headers,
          observe: 'response',
          params: params,
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        return this.http.patch<T>(url, JSON.stringify(body), options);
      }),
    ) as any;
  }

  protected deleteUrl(url: string): Observable<boolean> {
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options: any = {
          headers: this.headers,
          observe: 'response',
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        return this.http.delete(url, options).pipe(mergeMap((response: any) => [response.ok]));
      }),
    );
  }
}
